"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { parse as parseCsv } from "csv-parse/browser/esm/sync";
import { useMemo } from "react";
import { FileDownloadButton } from "@/src/components/file-viewer/file-download-button";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/src/components/shadcn/ui/table";
import { logger } from "@/src/lib/logging/logger";

// Renders per-row referral CSVs as a vertical label/value table (multi-row falls back to a table).
export function CSVViewer({
  url,
  filename,
  downloadDisabledReason
}: {
  url: string;
  filename: string;
  downloadDisabledReason?: string;
}) {
  const {
    data: text,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ["csv-viewer", url],
    queryFn: async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.text();
      } catch (err) {
        logger.error("CSVViewer failed to load file", {
          url,
          error: err instanceof Error ? err.message : String(err)
        });
        throw err;
      }
    },
    retry: false
  });
  const grid = useMemo(() => {
    if (!text) return null;
    try {
      return parseCsv(text, {
        bom: true,
        skip_empty_lines: true,
        relax_column_count: true,
        trim: true,
        relax_quotes: true
      }) as string[][];
    } catch (err_0) {
      logger.error("CSVViewer failed to parse file", {
        url,
        error: err_0 instanceof Error ? err_0.message : String(err_0)
      });
      return null;
    }
  }, [text, url]);
  if (isError) {
    return <div className="flex h-full items-center justify-center p-4 text-sm text-destructive">
        Error loading CSV: {error instanceof Error ? error.message : String(error)}
      </div>;
  }
  if (isLoading) {
    return <CSVLoadingSkeleton />;
  }
  if (!grid) {
    return <div className="flex h-full items-center justify-center p-4 text-sm text-destructive">
        Unable to display CSV: the file could not be parsed.
      </div>;
  }
  if (grid.length < 2) {
    return <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">Empty CSV.</div>;
  }
  const [header, ...dataRows] = grid;
  const isSingleRow = dataRows.length === 1;
  return <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      <div className="flex h-11 min-w-0 flex-shrink-0 items-center justify-end border-b p-2">
        <FileDownloadButton href={url} filename={filename} disabledReason={downloadDisabledReason} />
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-4 py-2">
        {isSingleRow ? <Table>
            <TableBody>
              {header.map((col, i) => <TableRow key={i}>
                  <TableHead className="h-auto w-1/2 py-2 align-top">{col}</TableHead>
                  <TableCell className="py-2 align-top break-words">{dataRows[0][i] ?? ""}</TableCell>
                </TableRow>)}
            </TableBody>
          </Table> : <Table>
            <TableHeader>
              <TableRow>
                {header.map((col_0, i_0) => <TableHead key={i_0}>{col_0}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataRows.map((row, r) => <TableRow key={r}>
                  {header.map((_, c) => <TableCell key={c} className="py-2 align-top break-words">
                      {row[c] ?? ""}
                    </TableCell>)}
                </TableRow>)}
            </TableBody>
          </Table>}
      </div>
    </div>;
}
export function CSVLoadingSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-11 min-w-0 flex-shrink-0 items-center border-b p-2"><Skeleton className="h-4 w-1/3" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex h-full w-full min-h-0 min-w-0 flex-col">{t0}<div className="flex-1 min-h-0 overflow-hidden p-4 space-y-2">{Array.from({
          length: 12
        }).map(_temp)}</div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, i) {
  return <Skeleton key={i} className="h-6 w-full" />;
}